import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../../components/design/DataTable';
import EmptyState from '../../../components/design/EmptyState';
import slaService from '../../../services/slaService';

export default function SLAPoliciesPage() {
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', row: null });
  const [form, setForm] = useState({
    name: '',
    firstResponseMinutes: '',
    resolutionMinutes: '',
    pauseOnPendingCustomer: true,
    useBusinessHours: false,
    active: true,
    nearDueMinutesFirstResponse: 10,
    nearDueMinutesResolution: 30
  });

  const load = async () => {
    setLoading(true);
    try {
      const resp = await slaService.listPolicies();
      setPolicies(resp?.policies || resp?.data?.policies || []);
    } catch (e) {
      setPolicies([]);
      toast.error(e?.message || 'Falha ao carregar políticas de SLA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setDialog({ open: true, mode: 'create', row: null });
    setForm({
      name: '',
      firstResponseMinutes: '',
      resolutionMinutes: '',
      pauseOnPendingCustomer: true,
      useBusinessHours: false,
      active: true,
      nearDueMinutesFirstResponse: 10,
      nearDueMinutesResolution: 30
    });
  };

  const openEdit = (row) => {
    setDialog({ open: true, mode: 'edit', row });
    setForm({
      name: row?.name || '',
      firstResponseMinutes: row?.firstResponseMinutes ?? '',
      resolutionMinutes: row?.resolutionMinutes ?? '',
      pauseOnPendingCustomer: row?.pauseOnPendingCustomer !== false,
      useBusinessHours: Boolean(row?.useBusinessHours),
      active: row?.active !== false,
      nearDueMinutesFirstResponse: row?.nearDueMinutesFirstResponse ?? 10,
      nearDueMinutesResolution: row?.nearDueMinutesResolution ?? 30
    });
  };

  const closeDialog = () => setDialog({ open: false, mode: 'create', row: null });

  const payload = useMemo(() => {
    const toNumOrNull = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return {
      name: String(form.name || '').trim(),
      firstResponseMinutes: toNumOrNull(form.firstResponseMinutes),
      resolutionMinutes: toNumOrNull(form.resolutionMinutes),
      pauseOnPendingCustomer: Boolean(form.pauseOnPendingCustomer),
      useBusinessHours: Boolean(form.useBusinessHours),
      active: Boolean(form.active),
      nearDueMinutesFirstResponse: toNumOrNull(form.nearDueMinutesFirstResponse) ?? 10,
      nearDueMinutesResolution: toNumOrNull(form.nearDueMinutesResolution) ?? 30
    };
  }, [form]);

  const save = async () => {
    if (!payload.name) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      if (dialog.mode === 'create') {
        await slaService.createPolicy(payload);
        toast.success('Política criada');
      } else {
        await slaService.updatePolicy(dialog.row.id, payload);
        toast.success('Política atualizada');
      }
      closeDialog();
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar política');
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>SLA</Typography>
        <Button variant="contained" onClick={openCreate}>Nova política</Button>
      </Box>

      <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Carregando...</Typography>
        ) : policies.length ? (
          <DataTable
            columns={[
              { key: 'name', label: 'Nome' },
              { key: 'firstResponseMinutes', label: '1ª resposta (min)', render: (v) => (v ?? '—') },
              { key: 'resolutionMinutes', label: 'Resolução (min)', render: (v) => (v ?? '—') },
              { key: 'useBusinessHours', label: 'Horário útil', render: (v) => (v ? 'Sim' : 'Não') },
              { key: 'pauseOnPendingCustomer', label: 'Pausa pendência', render: (v) => (v === false ? 'Não' : 'Sim') },
              { key: 'active', label: 'Ativa', render: (v) => (v === false ? 'Não' : 'Sim') },
              { key: 'actions', label: 'Ações', render: (_, row) => (
                <Button size="small" variant="outlined" onClick={() => openEdit(row)}>Editar</Button>
              )}
            ]}
            rows={policies}
            getKey={(r) => r.id}
          />
        ) : (
          <EmptyState title="Sem políticas de SLA" description="Crie a primeira política para vincular às filas." />
        )}
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialog.mode === 'create' ? 'Nova política de SLA' : 'Editar política de SLA'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="1ª resposta (min)"
                type="number"
                value={form.firstResponseMinutes}
                onChange={(e) => setForm(prev => ({ ...prev, firstResponseMinutes: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Resolução (min)"
                type="number"
                value={form.resolutionMinutes}
                onChange={(e) => setForm(prev => ({ ...prev, resolutionMinutes: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alerta 1ª resposta (min)"
                type="number"
                value={form.nearDueMinutesFirstResponse}
                onChange={(e) => setForm(prev => ({ ...prev, nearDueMinutesFirstResponse: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alerta resolução (min)"
                type="number"
                value={form.nearDueMinutesResolution}
                onChange={(e) => setForm(prev => ({ ...prev, nearDueMinutesResolution: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.useBusinessHours} onChange={(e) => setForm(prev => ({ ...prev, useBusinessHours: e.target.checked }))} />}
                label="Considerar horário útil (da fila)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.pauseOnPendingCustomer} onChange={(e) => setForm(prev => ({ ...prev, pauseOnPendingCustomer: e.target.checked }))} />}
                label="Pausar SLA em pendência do cliente"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />}
                label="Ativa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

